import { createContext, useContext, type ReactNode } from 'react'
import type {
  ImportWizardContextValue,
  ImportWizardRedirectContextValue,
  ImportWizardRealtimeContextValue,
  ImportWizardSnapshotContextValue,
  ImportWizardStableContextValue,
  ImportWizardSegmentationContextValue,
} from '@/types/components/audiences/import-wizard-context'

const ImportWizardStableContext =
  createContext<ImportWizardStableContextValue | null>(null)
const ImportWizardSnapshotContext =
  createContext<ImportWizardSnapshotContextValue | null>(null)
const ImportWizardRealtimeContext =
  createContext<ImportWizardRealtimeContextValue | null>(null)
const ImportWizardRedirectContext =
  createContext<ImportWizardRedirectContextValue | null>(null)
const ImportWizardSegmentationContext =
  createContext<ImportWizardSegmentationContextValue | null>(null)

export function useOptionalImportWizardStableContext(): ImportWizardStableContextValue | null {
  return useContext(ImportWizardStableContext)
}

export function useImportWizardStableContext(): ImportWizardStableContextValue {
  const ctx = useContext(ImportWizardStableContext)
  if (!ctx) {
    throw new Error(
      'useImportWizardStableContext must be used within ImportWizardProvider. ' +
        'Ensure your component renders inside the audience import SessionRoute layout.',
    )
  }
  return ctx
}

export function useImportWizardSnapshotContext(): ImportWizardSnapshotContextValue {
  const ctx = useContext(ImportWizardSnapshotContext)
  if (!ctx) {
    throw new Error(
      'useImportWizardSnapshotContext must be used within ImportWizardProvider. ' +
        'Ensure your component renders inside the audience import SessionRoute layout.',
    )
  }
  return ctx
}

export function useImportWizardRealtimeContext(): ImportWizardRealtimeContextValue {
  const ctx = useContext(ImportWizardRealtimeContext)
  if (!ctx) {
    throw new Error(
      'useImportWizardRealtimeContext must be used within ImportWizardProvider. ' +
        'Ensure your component renders inside the audience import SessionRoute layout.',
    )
  }
  return ctx
}

export function useOptionalImportWizardRealtimeContext(): ImportWizardRealtimeContextValue | null {
  return useContext(ImportWizardRealtimeContext)
}

export function useImportWizardRedirectContext(): ImportWizardRedirectContextValue {
  const ctx = useContext(ImportWizardRedirectContext)
  if (!ctx) {
    throw new Error(
      'useImportWizardRedirectContext must be used within ImportWizardProvider. ' +
        'Ensure your component renders inside the audience import SessionRoute layout.',
    )
  }
  return ctx
}

/**
 * Access the wizard-level data when used inside the layout's provider.
 * Returns null when outside ImportWizardProvider (e.g. standalone usage).
 */
export function useOptionalImportWizardContext(): ImportWizardContextValue | null {
  const stable = useContext(ImportWizardStableContext)
  const snapshot = useContext(ImportWizardSnapshotContext)
  const realtime = useContext(ImportWizardRealtimeContext)
  const redirect = useContext(ImportWizardRedirectContext)
  const segmentation = useContext(ImportWizardSegmentationContext)
  if (!stable || !snapshot || !realtime || !redirect || !segmentation) {
    return null
  }
  return {
    ...stable,
    ...snapshot,
    ...realtime,
    ...redirect,
    ...segmentation,
  }
}

/**
 * Access the wizard-level data owned by the SessionRoute layout.
 *
 * Must be called from a component rendered inside the layout's provider;
 * throws immediately if the context is missing (programming error).
 */
export function useImportWizardContext(): ImportWizardContextValue {
  const ctx = useOptionalImportWizardContext()
  if (!ctx) {
    throw new Error(
      'useImportWizardContext must be used within ImportWizardProvider. ' +
        'Ensure your component renders inside the audience import SessionRoute layout.',
    )
  }
  return ctx
}

export function ImportWizardProvider({
  value,
  children,
}: {
  value: ImportWizardContextValue
  children: ReactNode
}) {
  const stableValue: ImportWizardStableContextValue = {
    sessionId: value.sessionId,
    registerImportSessionEventHandler: value.registerImportSessionEventHandler,
    navigateToStep: value.navigateToStep,
    setLastMutationRequestId: value.setLastMutationRequestId,
    activeStepKey: value.activeStepKey,
    goToStep: value.goToStep,
  }
  const snapshotValue: ImportWizardSnapshotContextValue = {
    snapshot: value.snapshot,
    isSnapshotLoading: value.isSnapshotLoading,
    isSnapshotValidating: value.isSnapshotValidating,
    snapshotError: value.snapshotError,
    mutateSnapshot: value.mutateSnapshot,
    importSteps: value.importSteps,
  }
  const realtimeValue: ImportWizardRealtimeContextValue = {
    isRealtimeConnected: value.isRealtimeConnected,
  }
  const redirectValue: ImportWizardRedirectContextValue = {
    suppressRequiredFieldsRedirect: value.suppressRequiredFieldsRedirect,
    setSuppressRequiredFieldsRedirect: value.setSuppressRequiredFieldsRedirect,
    setRequiredFieldsRedirectHold: value.setRequiredFieldsRedirectHold,
  }
  const segmentationValue: ImportWizardSegmentationContextValue = {
    hiddenFilterColumns: value.hiddenFilterColumns,
    setHiddenFilterColumns: value.setHiddenFilterColumns,
    customFilterLabels: value.customFilterLabels,
    setCustomFilterLabels: value.setCustomFilterLabels,
    audienceTags: value.audienceTags,
    setAudienceTags: value.setAudienceTags,
  }

  return (
    <ImportWizardStableContext.Provider value={stableValue}>
      <ImportWizardSnapshotContext.Provider value={snapshotValue}>
        <ImportWizardRealtimeContext.Provider value={realtimeValue}>
          <ImportWizardRedirectContext.Provider value={redirectValue}>
            <ImportWizardSegmentationContext.Provider value={segmentationValue}>
              {children}
            </ImportWizardSegmentationContext.Provider>
          </ImportWizardRedirectContext.Provider>
        </ImportWizardRealtimeContext.Provider>
      </ImportWizardSnapshotContext.Provider>
    </ImportWizardStableContext.Provider>
  )
}
